/**
 * Typoglycemia canonicalization for guardrail pattern matching.
 *
 * Same algorithm as guardrail text normalization: sort interior letters (first/last fixed)
 * for alphabetic words in a bounded length range. Used to generate regex alternates so
 * literals match both dictionary spellings and canonicalized scrambled forms.
 */

const MIN_WORD_LENGTH_FOR_CANONICALIZATION = 4;
const MAX_WORD_LENGTH_FOR_CANONICALIZATION = 12;

const ALPHABETIC_WORD_PATTERN = /^[a-zA-Z]+$/;

const REGEX_META_PATTERN = /[.*+?^${}()|[\]\\]/g;

/**
 * Escapes a string for use as a literal segment inside a RegExp source.
 */
export function escapeRegexLiteral(text: string): string {
  return text.replace(REGEX_META_PATTERN, "\\$&");
}

/**
 * Canonicalizes a single word (typoglycemia transform). See guardrail normalize pipeline.
 */
export function canonicalizeTypoglycemiaWord(word: string): string {
  const isTooShort = word.length < MIN_WORD_LENGTH_FOR_CANONICALIZATION;
  const isTooLong = word.length > MAX_WORD_LENGTH_FOR_CANONICALIZATION;
  if (isTooShort || isTooLong) return word;

  const isAlphabeticOnly = ALPHABETIC_WORD_PATTERN.test(word);
  if (!isAlphabeticOnly) return word;

  const firstCharacter = word[0];
  const lastCharacter = word[word.length - 1];
  const sortedInteriorCharacters = word.slice(1, -1).split("").sort().join("");
  return firstCharacter + sortedInteriorCharacters + lastCharacter;
}

/**
 * Applies typoglycemia canonicalization to every letter-run in the text.
 */
export function canonicalizeTypoglycemiaText(text: string): string {
  return text.replace(/[a-zA-Z]+/g, (word) =>
    canonicalizeTypoglycemiaWord(word),
  );
}

/**
 * Builds a non-capturing alternation of dictionary words plus their typoglycemia canonical
 * forms (when different). Longer strings are ordered first so alternation prefers whole
 * words (e.g. "instructions" before "instruction").
 */
export function buildTypoglycemiaRegexAlternation(
  words: readonly string[],
): string {
  const variants: string[] = [];
  const seen = new Set<string>();

  for (const raw of words) {
    const word = raw.toLowerCase();
    if (!seen.has(word)) {
      seen.add(word);
      variants.push(word);
    }
    const canonical = canonicalizeTypoglycemiaWord(word);
    if (canonical !== word && !seen.has(canonical)) {
      seen.add(canonical);
      variants.push(canonical);
    }
  }

  variants.sort((a, b) => b.length - a.length);
  return variants.map(escapeRegexLiteral).join("|");
}
